import React, { useEffect, useRef, useState } from "react";
import { Button, Modal, notification, Spin, Steps } from "antd";
import { ConnectedProps, useDispatch } from "react-redux";
import { LoadingOutlined } from "@ant-design/icons";

import cssLogo from "../../images/css-logo-registration.svg";
import StepOne from "./StepOne";
import StepTwo from "./StepTwo";
import StepThree from "./StepThree";
import connector from "./Container";
import {
  type ParticipantsSignleBasedReqType,
  type PublicRegisterFormValuesType,
  type CompanyAndParticipantsReqType,
  type PackagePricingType,
} from "../../models";
import { IS_SUCCESSFULLY_REGISTERED } from "../Participants/modules/types";
import {
  injectGtm,
  pushAddPaymentInfo,
  pushPurchase,
  pushGenerateLead,
  PENDING_PURCHASE_KEY,
  type PendingPurchaseData,
  type PendingLeadData,
} from "../../utils/gtm";
import { ApiService } from "../../services/apiServices";
import Footer from "../Footer";

// Delays before each backend payment check, in ms: six attempts over ~19s.
// Monri's server-to-server callback and the browser redirect race each other,
// so the first check can legitimately come back unpaid.
const PURCHASE_CHECK_DELAYS = [0, 1000, 2000, 3000, 5000, 8000];

// Order numbers whose check sequence has already been started in this page load.
// Module scope on purpose: it has to outlive a remount of this component, and
// the mount effect runs twice per page load under React StrictMode
// (mount -> unmount -> remount) in development. A component ref or state would
// be recreated by that remount and would let a second sequence start.
// Cleared only by a full page load, which is exactly when a retry is wanted.
const startedPurchaseChecks = new Set<string>();

const Register = ({ state, actions }: ConnectedProps<typeof connector>) => {
  const dispatch = useDispatch();

  const {
    packagePricing,
    eventInfo,
    isLoading,
    isSuccessfullyRegistered,
    searchedCompanies,
  } = state;
  const [current, setCurrent] = useState(0);
  const [bundle, setBundle] = useState<PackagePricingType | null>(null);
  const [wayOfParticipation, setWayOfParticipation] = useState<string | null>(
    null
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  // Payment parameters as priced and signed by the backend. The browser no
  // longer derives an amount, an order number or a digest of its own.
  const [preparedPayment, setPreparedPayment] = useState<any>(null);
  const [checkoutValues, setCheckoutValues] =
    useState<PublicRegisterFormValuesType | null>(null);
  const [isPreparingPayment, setIsPreparingPayment] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<string>("");
  const [selectedCompany, setSelectedCompany] = useState<any>("");
  // Snapshot ecommerce podataka za generate_lead, popunjen u onFinish.
  // Consume-once: effect ga čita i odmah nulira -> nema duplog eventa na
  // re-renderu ili SPA back-navigaciji (Redux flag ostaje true nakon remounta).
  const pendingLeadRef = useRef<PendingLeadData | null>(null);

  // Runs after the modal has rendered, so #monri-button-container exists.
  useEffect(() => {
    preparedPayment && checkoutValues && paymentCheckout(checkoutValues, preparedPayment);
  }, [preparedPayment]);


  useEffect(() => {
    if (eventInfo?.[0]?.gtmContainerId) {
      injectGtm(eventInfo[0].gtmContainerId);
    }
  }, [eventInfo]);

  useEffect(() => {
    if (!isSuccessfullyRegistered || paymentMethod !== "predračun") return;
    const lead = pendingLeadRef.current;
    if (!lead) return;
    pendingLeadRef.current = null;
    pushGenerateLead(lead);
  }, [isSuccessfullyRegistered, paymentMethod]);

  /**
   * Reports `purchase` to GA4 only once the Eventio backend confirms the card
   * payment was processed. A participant row carrying the order number can only
   * exist after a digest-verified "approved" Monri callback, so this replaces
   * trusting the `transaction+approved` substring in the return URL.
   *
   * Runs in the background: it never touches component state and never shows
   * anything to the user, so a slow or failing backend degrades analytics only.
   */
  const confirmAndPushPurchase = async () => {
    const pendingStr = sessionStorage.getItem(PENDING_PURCHASE_KEY);
    if (!pendingStr) return;

    let pending: PendingPurchaseData;
    try {
      pending = JSON.parse(pendingStr);
    } catch {
      return;
    }
    if (!pending?.orderNumber) return;

    // One sequence per order per page load. The loop below is detached from
    // React — it holds no state and is never cancelled — so without this a
    // remount would start a second sequence running alongside the first.
    if (startedPurchaseChecks.has(pending.orderNumber)) return;
    startedPurchaseChecks.add(pending.orderNumber);

    for (const delay of PURCHASE_CHECK_DELAYS) {
      if (delay) {
        await new Promise((resolve) => setTimeout(resolve, delay));
      }

      try {
        // Per-call timeout: ApiService has no global one, and a hung socket
        // would otherwise stall this loop past its ~19s budget indefinitely.
        const { data } = await ApiService.get(
          `/payment-status/${pending.orderNumber}`,
          { timeout: 5000 }
        );

        // Match the order back: a slow reply from an earlier checkout in the
        // same session must not be read as a confirmation for this one.
        if (data?.orderNumber === pending.orderNumber && data?.paid === true) {
          // Dedup stays where it already is, inside pushPurchase().
          pushPurchase(pending);
          sessionStorage.removeItem(PENDING_PURCHASE_KEY);
          return;
        }
      } catch {
        // Network error or 5xx — treat as "not confirmed yet" and spend the
        // attempt rather than aborting the whole check.
      }
    }

    // Never confirmed: no event, no purchase_sent marker, nothing shown to the
    // user. The pending payload is left in place so a refresh can retry.
  };

  useEffect(() => {
    actions.getPackagePricing();
    actions.getEventInfo();

    let search = window?.location?.search;
    let isSuccess = search.includes("transaction+approved");
    let isDeclined = search.includes("declined");

    // The return URL drives the UX only — success screen and failure notice
    // behave exactly as before.
    if (isSuccess) {
      dispatch({ type: IS_SUCCESSFULLY_REGISTERED, payload: true });
    }

    isDeclined &&
      notification.error({
        message: "Payment Failed",
      });

    // Analytics is verified against the backend instead, never against the
    // return URL. Deliberately not gated on `isSuccess`: it runs whenever a
    // checkout was started in this tab, so it does not depend on Monri's
    // return URL format, which is configured outside this repo.
    confirmAndPushPurchase();
  }, []);

  const isStepDisabled = (current: number) => {
    switch (true) {
      case current === 0:
        return false;
      case current === 1:
        return bundle === null;
      case current === 2:
        return bundle === null || wayOfParticipation === null;
    }
  };

  const registerCompanyAndParticipants = (
    values: PublicRegisterFormValuesType
  ) => {
    const companyAndParticipants: CompanyAndParticipantsReqType = {
      entityId: packagePricing[0]?.entityId,
      packagePricingId: bundle?.id,
      sendInvoice: values.payment === "predračun",
      marketingConsent: values.marketingConsent ?? false,
      company: !selectedCompany
        ? {
            name: values.companyName,
            address: values.address,
            postalCodeAndCity: values.postalCodeAndCity,
            country: values.country,
            email: values.companyEmail,
            phoneNumber: values.phoneNumber,
            idNumber: values.ID,
            vatNumber: values.PDV ? values.PDV : null,
          }
        : null,
      companyId: selectedCompany?.id ? selectedCompany.id : null,
      participants: values.participants,
    };

    actions.publicRegisterCompanyAndParticipants(companyAndParticipants);
  };

  const registerParticipants = (values: PublicRegisterFormValuesType) => {
    const participants: ParticipantsSignleBasedReqType = {
      entityId: packagePricing[0]?.entityId,
      packagePricingId: bundle?.id,
      sendInvoice: values.payment === "predračun",
      marketingConsent: values.marketingConsent ?? false,
      participants: values.participants,
    };

    actions.publicRegisterParticipants(participants);
  };

  const handlePaymentMethodChange = (paymentType: string, quantity: number) => {
    if (!bundle || !eventInfo?.[0]) return;
    const unitPrice =
      eventInfo[0].priceType === "regular"
        ? Number(bundle.price)
        : Number(bundle.earlyPrice);
    const vat = eventInfo[0].vat ? 1.17 : 1;
    const totalValue = Math.round(unitPrice * vat * quantity * 100) / 100;
    pushAddPaymentInfo(
      bundle.package,
      unitPrice,
      quantity,
      totalValue,
      paymentType === "online" ? "card" : "invoice"
    );
  };

  const onFinish = (values: PublicRegisterFormValuesType) => {
    if (values.honeypot) return;

    setPaymentMethod(values.payment);

    if (values.payment === "predračun" && bundle && eventInfo?.[0]) {
      const unitPrice =
        eventInfo[0].priceType === "regular"
          ? Number(bundle.price)
          : Number(bundle.earlyPrice);
      const vat = eventInfo[0].vat ? 1.17 : 1;
      const quantity = values.participants.length;
      pendingLeadRef.current = {
        bundlePackage: bundle.package,
        unitPrice,
        quantity,
        totalValue: Math.round(unitPrice * vat * quantity * 100) / 100,
      };
    }

    if (values.payment === "online") {
      startCardPayment(values);
      return;
    }

    wayOfParticipation === "PRAVNO LICE"
      ? registerCompanyAndParticipants(values)
      : registerParticipants(values);
  };

  const buildPreparePayload = (values: PublicRegisterFormValuesType) => ({
    entityId: packagePricing[0]?.entityId,
    packagePricingId: bundle?.id,
    marketingConsent: values.marketingConsent ?? false,
    company:
      wayOfParticipation === "PRAVNO LICE" && !selectedCompany
        ? {
            name: values.companyName,
            address: values.address,
            postalCodeAndCity: values.postalCodeAndCity,
            country: values.country,
            email: values.companyEmail,
            phoneNumber: values.phoneNumber,
            idNumber: values.ID,
            vatNumber: values.PDV ? values.PDV : null,
          }
        : null,
    companyId:
      wayOfParticipation === "PRAVNO LICE" && selectedCompany?.id
        ? selectedCompany.id
        : null,
    participants: values.participants,
  });

  /**
   * Asks the backend to price and sign the checkout, then opens Monri with the
   * values it returned. The amount, order number and digest are all decided
   * server-side; this component only renders what it is given.
   */
  const startCardPayment = async (values: PublicRegisterFormValuesType) => {
    setIsPreparingPayment(true);
    try {
      const { data } = await ApiService.post(
        "/payment/prepare",
        buildPreparePayload(values)
      );

      // GA4 purchase reads this back after the Monri redirect, so it carries the
      // backend's numbers rather than a second calculation of our own.
      const pending: PendingPurchaseData = {
        orderNumber: data.orderNumber,
        bundlePackage: data.display.packageType,
        unitPrice: data.display.unitPrice,
        quantity: data.display.quantity,
        totalValue: data.display.totalValue,
      };
      sessionStorage.setItem(PENDING_PURCHASE_KEY, JSON.stringify(pending));

      setCheckoutValues(values);
      setIsModalOpen(true);
      setPreparedPayment(data);
    } catch (err: any) {
      notification.error({
        message: err?.response?.data?.message
          ? `${err.response.data.message}!`
          : "Something went wrong",
      });
    } finally {
      setIsPreparingPayment(false);
    }
  };

  const registerSteps = () => {
    switch (current) {
      case 0:
        return (
          <StepOne
            packagePricing={packagePricing}
            eventInfo={eventInfo}
            setBundle={setBundle}
            setCurrent={setCurrent}
          />
        );
      case 1:
        return (
          <StepTwo
            setWayOfParticipation={setWayOfParticipation}
            setCurrent={setCurrent}
          />
        );
      case 2:
        return (
          <StepThree
            wayOfParticipation={wayOfParticipation}
            onFinish={onFinish}
            isLoading={isLoading || isPreparingPayment}
            searchedCompanies={searchedCompanies}
            getSearchedCompanies={actions.getSearchedCompanies}
            selectedCompany={selectedCompany}
            setSelectedCompany={setSelectedCompany}
            onPaymentMethodChange={handlePaymentMethodChange}
          />
        );
    }
  };

  const paymentCheckout = (
    values: PublicRegisterFormValuesType,
    prepared: any
  ) => {
    const scriptElement = document.createElement("script");
    scriptElement.src = prepared.payment.lightboxUrl;
    scriptElement.className = "lightbox-button";
    scriptElement.setAttribute(
      "data-authenticity-token",
      prepared.payment.authenticityToken
    );
    scriptElement.setAttribute("data-amount", `${prepared.payment.amount}`);
    scriptElement.setAttribute("data-currency", prepared.payment.currency);
    scriptElement.setAttribute(
      "data-ch-full-name",
      `${values.participants[0].firstName} ${values.participants[0].lastName}`
    );
    scriptElement.setAttribute(
      "data-ch-city",
      values.participants[0].postalCodeAndCity?.split(" ")[1]
        ? values.participants[0].postalCodeAndCity
            ?.split(" ")
            .slice(1)
            .join(" ")
        : ""
    );
    scriptElement.setAttribute(
      "data-ch-zip",
      values.participants[0].postalCodeAndCity
        ? values.participants[0].postalCodeAndCity.split(" ")[0]
        : ""
    );
    scriptElement.setAttribute(
      "data-ch-address",
      values.participants[0].address ? values.participants[0].address : ""
    );
    scriptElement.setAttribute(
      "data-ch-country",
      values.participants[0].country ? values.participants[0].country : ""
    );
    scriptElement.setAttribute("data-order-info", "Purchase");
    scriptElement.setAttribute("data-digest", prepared.payment.digest);
    scriptElement.setAttribute("data-language", "en");
    scriptElement.setAttribute("data-order-number", prepared.orderNumber);
    scriptElement.setAttribute(
      "data-transaction-type",
      prepared.payment.transactionType
    );
    scriptElement.setAttribute(
      "data-ch-phone",
      values.participants[0].phoneNumber
        ? values.participants[0].phoneNumber
        : ""
    );
    scriptElement.setAttribute(
      "data-ch-email",
      values.participants[0].email ? values.participants[0].email : ""
    );

    scriptElement.setAttribute(
      // Only the order identifier. The registration payload, the company and
      // the pricing now live in the backend's PaymentOrder snapshot, so none of
      // it has to travel through the browser and back via the callback.
      "data-custom-params",
      JSON.stringify({ orderNumber: prepared.orderNumber })
    );

    const formElement = document.createElement("form");
    formElement.appendChild(scriptElement);
    document.getElementById("monri-button-container")!.innerHTML = "";
    document.getElementById("monri-button-container")!.appendChild(formElement);
  };

  return !isLoading ? (
    <div className="min-h-screen flex flex-col justify-between bg-registration-blue">
      <div>
        <div className="bg-registration-blue h-[100px] xl:h-[130px] pl-[5%] xl:pl-[8%] flex items-end">
          <img src={cssLogo} alt="cssLogo" className=" h-[60px] md:h-[70px]" />
        </div>
        {eventInfo?.[0]?.isComingSoon ? (
          <>
            <div className="flex flex-col justify-center items-center text-center gap-6 my-20">
              <div className="text-white text-[22px] leading-[30px] md:text-[34px] md:leading-[42px] lg:text-[40px] lg:leading-[48px] 2xl:text-[48px] 2xl:leading-[58px] font-black">
                REGISTRACIJU
                <div>OSTVARAMO USKORO!</div>
              </div>
              <div className="text-white  w-[95%] sm:w-[85%] m-auto text-[16px] lg:text-[20px]  xl:text-[18px]  2xl:text-[22px] mb-6">
                Hvala vam na iskazanom povjerenju.
              </div>
              <div>
                <Button
                  htmlType="button"
                  className="text-white bg-custom-pink-900 border border-white  sm:w-[210px] h-10 sm:text-[16px] font-bold  rounded-[2px] shadow mb-10"
                >
                  <a href="https://yes-css.ba/">NAZAD NA POČETNU</a>
                </Button>
              </div>
            </div>
          </>
        ) : eventInfo?.[0] && !eventInfo?.[0].isSoldOut ? (
          <>
            {!isSuccessfullyRegistered ? (
              <>
                <div>
                  <div className="pt-4 md:pt-0 text-center text-white text-[30px] sm:text-[38px] 2xl:text-[48px] font-lato font-black">
                    Registracija
                  </div>
                  <div className="md:w-[80%] lg:w-[60%] m-auto ">
                    <Steps
                      responsive={false}
                      type="navigation"
                      size="small"
                      current={current}
                      onChange={(value) => setCurrent(value)}
                      className="site-navigation-steps"
                      items={[
                        {
                          title: "Korak 1",
                          disabled: isStepDisabled(0),
                          status:
                            current === 0
                              ? "process"
                              : bundle
                              ? "finish"
                              : "wait",
                        },
                        {
                          title: "Korak 2",
                          disabled: isStepDisabled(1),

                          status:
                            current === 1
                              ? "process"
                              : wayOfParticipation
                              ? "finish"
                              : "wait",
                        },
                        {
                          title: "Korak 3",
                          disabled: isStepDisabled(2),
                        },
                      ]}
                    />
                  </div>
                </div>
                <div>{registerSteps()}</div>
              </>
            ) : (
              <div className="flex justify-center text-center my-20">
                <div className="flex flex-col gap-6">
                  <div className="text-white text-[22px] leading-[30px] md:text-[34px] md:leading-[42px] lg:text-[40px] lg:leading-[48px] 2xl:text-[48px] 2xl:leading-[58px] font-black">
                    Hvala vam što ste odlučili da
                    <div> nam ukažete povjerenje!</div>
                  </div>
                  <div className="text-white  w-[95%] md:w-[75%] xl:w-[85%] m-auto text-[11px] md:text-[14px]  lg:text-[18px]  2xl:text-[22px] mb-6">
                    {paymentMethod === "predračun"
                      ? "Nakon što izvršite uplatu predračuna svi registrovani učesnici će na navedene e-mail adrese dobiti ulaznice za konferenciju."
                      : "Svi registrovani učesnici su na navedene e-mail adrese dobili ulaznice za konferenciju."}
                  </div>
                  <div>
                    <Button
                      htmlType="button"
                      className="text-white bg-custom-pink-900 border border-custom-pink-900  sm:w-[210px] h-10 sm:text-[16px] font-bold  rounded-[2px] shadow mb-10"
                    >
                      <a href="https://yes-css.ba/">NAZAD NA POČETNU</a>
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            <div className="flex flex-col justify-center items-center text-center gap-6 my-20">
              <div className="text-white text-[22px] leading-[30px] md:text-[34px] md:leading-[42px] lg:text-[40px] lg:leading-[48px] 2xl:text-[48px] 2xl:leading-[58px] font-black">
                SVE ULAZNICE SU
                <div>RASPRODATE!</div>
              </div>
              <div className="text-white  w-[95%] sm:w-[85%] m-auto text-[16px] lg:text-[20px]  xl:text-[18px]  2xl:text-[22px] mb-6">
                Hvala vam na ukazanom povjerenju.
              </div>
              <div>
                <Button
                  htmlType="button"
                  className="text-white bg-custom-pink-900 border border-white  sm:w-[210px] h-10 sm:text-[16px] font-bold  rounded-[2px] shadow mb-10"
                >
                  <a href="https://yes-css.ba/">NAZAD NA POČETNU</a>
                </Button>
              </div>
            </div>
          </>
        )}

        <Modal
          title="Idi na plaćanje"
          className="text-center mt-32"
          open={isModalOpen}
          onCancel={() => setIsModalOpen(false)}
          okButtonProps={{ style: { display: "none" } }}
          cancelButtonProps={{ style: { display: "none" } }}
        >
          <div id="monri-button-container"></div>
        </Modal>
      </div>
      <Footer />
    </div>
  ) : (
    <div className="bg-registration-blue w-full h-screen flex justify-center items-center ">
      <Spin indicator={<LoadingOutlined style={{ fontSize: 46 }} spin />} />
    </div>
  );
};

export default Register;
